export const SITE_OWNER_EMAIL = 'owner@attackyouradventure.com';

export const SITE_PAGE_IDS = ['home', 'about', 'rules', 'terms', 'privacy'];

export const STRUCTURED_PAGE_IDS = ['rules'];

export const MULTI_SECTION_PAGE_IDS = ['about', 'terms', 'privacy'];

/**
 * Policy pages are edited in code, not in the browser, so every change to legal
 * text has version history. Firestore overwrites in place and keeps no previous
 * version, which is no good for answering "what did the Terms say on the day
 * this member booked?". Marketing copy (home, about) stays browser-editable.
 * Enforced for real in firestore.rules; hiding the editor is only the UI half.
 */
export const CODE_MANAGED_PAGE_IDS = ['rules', 'terms', 'privacy'];

export function isCodeManagedPage(pageId) {
  return CODE_MANAGED_PAGE_IDS.includes(pageId);
}

export const SECTION_PARAGRAPH_COUNT = 6;

export const DEFAULT_SITE_CONTENT = {
  home:
    'ATTACK! Your Adventure exists to help people get outside — new places, new activities, and real conversations with people who know the area.\n\nBorrow gear for a trip instead of buying something else to sit in the garage. List what you already use. Post what you need. Message owners, renters, or local experts who can point you toward a good trail, river, or camp spot.\n\nSearch below to find gear near you. Read About for the longer version of why we built this, or FAQ for payments, cancellations, and listings.',
  about: [
    'ATTACK! Your Adventure started with a simple belief: people are happier when they are outside having real experiences — not when they are chasing the next purchase.',
    'We built a community around sharing gear and local knowledge. Rent a kayak for a weekend, borrow a tent for a road trip, or talk to someone who actually knows the trails near town. The equipment is shared so the barrier is lower; the point is getting out there.',
    'Owners list gear they already use. Renters find what they need without buying new. Local experts opt in to answer questions about their area — trails, water, campsites, things you will not find in a product review.',
    'Payments stay on the platform through Stripe. Messaging keeps coordination simple. Reviews after completed rentals help the next person choose with confidence. However you use the site — renting, listing, asking a local — you are part of a community that would rather be on a trail than scrolling for gear.',
  ],
  rules: [
    {
      title: '',
      paragraphs: [
        'At ATTACK! Your Adventure, everything comes down to one principle: be a good human. Treat others with respect and take responsibility for your actions. Equipment gets damaged from time to time, and most of the time it isn\'t intentional. We\'ve built a system to handle those situations fairly. If you cause damage, own it and make it right. If your item comes back with minor wear, stay reasonable—don\'t expect full replacement value for normal use. We\'d rather everyone handle these situations like adults instead of requiring us to step in.',
      ],
      bullets: [],
    },
    {
      title: 'How Rentals Work',
      paragraphs: [
        'When you list an item, you set the available dates, your rental price, and a Condition Guarantee amount (up to $2,500). This guarantee acts as a security hold on the renter\'s card and helps protect you if the item is returned damaged.',
        'Here\'s how the process works:',
      ],
      bullets: [
        'A potential renter sends a request to book your item.',
        'Once you approve the request, the renter can complete payment through the My Bookings page.',
        'Payment is processed two days before the rental start date. If the booking is made within two days of the start date, it is charged immediately.',
        'A pending hold is placed on the renter\'s card for the Condition Guarantee amount you selected.',
        'When the item is returned in satisfactory condition, you release the hold.',
        'If the item is not returned in good condition and the two parties cannot resolve it directly, the owner may open a dispute. This notifies our team at admin@attackyouradventure.com. We will reach out to both parties and help determine the appropriate outcome. Funds are only released to the owner with the renter\'s acknowledgement.',
      ],
      paragraphsAfterBullets: [
        'We strongly encourage users to communicate directly and work through issues like adults. The dispute process exists as a last resort and a layer of protection for everyone involved.',
      ],
    },
    {
      title: 'Cancellations',
      paragraphs: [
        'You can cancel a booking any time before it starts. What happens to the money depends on when you cancel, and who cancels. Timing is measured from the rental start date and the 48-hour payment window (when the renter\'s card is charged).',
      ],
      bullets: [
        'More than 48 hours before the start date: no payment is taken, or a full automatic refund is issued if the card was already charged. Any condition hold is released and the dates open back up on the owner\'s calendar.',
        'Within 48 hours, renter cancels: payment has already been processed and is not refunded — the owner keeps it. The dates are released so the owner has a chance to re-rent them, and any condition hold is removed. If something has come up, message the owner first; if the two of you agree on a refund, email admin@attackyouradventure.com and we will honor it.',
        'Within 48 hours, owner cancels: the renter is refunded in full automatically, and that amount is reversed out of the owner\'s payout. Those dates stay blocked on the owner\'s calendar for the original rental period and cannot be re-rented to someone else. Any condition hold is removed.',
      ],
      paragraphsAfterBullets: [
        'Cancelling late lands on someone else. An owner who cancels the day before leaves a renter without gear for a trip they have already planned, and a renter who cancels late takes dates off the calendar that someone else could have used. Communicate early, and handle it the way you would want it handled.',
      ],
    },
    {
      title: 'Getting Paid (Stripe Onboarding)',
      paragraphs: [
        'To receive payouts, complete Stripe Connect onboarding on your profile. After you approve a booking, the renter saves a card. Their card is charged 48 hours before the rental starts (or immediately if booked inside that window). When that charge processes, you receive 90% in your connected Stripe account and ATTACK! Your Adventure keeps 10%. Stripe deposits to your bank on its own schedule.',
      ],
      bullets: [],
    },
    {
      title: 'Local Experts',
      paragraphs: [
        'When creating or editing your profile, you have the option to identify yourself as a Local Expert. If you know your area well and enjoy helping others discover great places to explore, select "yes." This allows other users to message you directly with questions about activities and locations—even if they aren\'t renting one of your listed items.',
      ],
      bullets: [],
    },
    {
      title: 'Community Expectations',
      paragraphs: [
        'We built this platform around the idea of leaving things better than you found them. Take care of the equipment you rent, respect the outdoors, and treat other users with consideration. Building genuine connections is part of the experience, but please use common sense: meet in public places when possible, and look out for one another.',
        'Don\'t be the person who takes advantage of the system or others. Take care of the gear, take care of nature, and take care of each other.',
      ],
      bullets: [],
    },
  ],
  terms: [
    'ATTACK! Your Adventure is a peer-to-peer gear rental marketplace. We connect gear owners with renters but are not a party to the rental agreement between members. Owners are responsible for accurately describing gear condition, availability, and pickup or delivery arrangements. Renters are responsible for using gear safely, returning it on time, and in the same condition as received, ordinary wear excepted.',
    'Payments are processed through Stripe. After a rental is approved, renters save a payment method to secure the booking. The rental charge is processed 48 hours before the rental start date (or immediately if the booking is made inside that window). Platform fees and owner payouts are handled according to our published rental rules. A separate condition guarantee authorization may be placed on the renter\'s card after the rental charge for eligible listings. The gear owner releases that authorization after a successful return unless damage is reported and approved under our damage process.',
    'Cancellations follow the timing rules published on the site. Either member may cancel a booking before it starts. Cancelling more than 48 hours before the rental start date results in no charge, or a full automatic refund if the card was already charged. If a renter cancels within 48 hours of the start date, the booking is not refunded and the owner keeps the payment. If an owner cancels within 48 hours of the start date, the renter is refunded in full and that amount is reversed out of the owner\'s payout. Failure to return gear on time, misrepresentation, abusive behavior, or unsafe use may result in account suspension and additional charges where permitted by law.',
    'Outdoor recreation carries real risk. Kayaks, paddleboards, RVs, trailers, camping equipment, and similar gear can cause serious injury or death — from equipment failure, weather, water, terrain, or simple user error. You find, rent, lend, and use gear through ATTACK! Your Adventure entirely at your own risk. We do not own, inspect, maintain, test, or certify any item listed on this site, and we are not present for any rental. Renters are responsible for confirming that gear is in safe working order before using it, for being trained and physically able to use it, and for following all applicable laws — including licensing, registration, and insurance requirements for vehicles and watercraft. Owners are responsible for honestly describing the condition of what they list and for not listing gear they know to be unsafe.',
    'To the fullest extent permitted by law, ATTACK! Your Adventure and its operators are not liable for any injury, death, property damage, or other loss arising from the rental, use, transport, or storage of any item arranged through this site, and you agree to assume those risks yourself. Some states limit how liability may be disclaimed, so parts of this section may not apply to you. By using this site you agree to communicate respectfully, provide accurate information, and comply with applicable laws. These terms may be updated from time to time, and continued use after changes are posted constitutes acceptance of the updated terms. Questions can be sent through the Contact Us page.',
  ],
  privacy: [
    'We collect information you provide when creating an account, listing gear, requesting rentals, messaging other members, submitting reviews, or contacting us. This may include your name, email address, phone number, profile details, gear listings, rental history, messages, and payment-related identifiers processed by Stripe.',
    'We use Firebase for authentication, database, file storage, and hosting. Payments and owner payouts are processed by Stripe. Transactional email — booking updates, cancellation notices, and reminders — is sent through SendGrid. We do not store full payment card numbers on our servers. Stripe processes payment and identity information according to its own privacy policy. We use this information to operate the marketplace, prevent fraud, provide customer support, and improve the service.',
    'Gear listings, profile names, reviews, and messages may be visible to other users according to how the product works. Admin staff can access information needed to resolve disputes, payment issues, safety concerns, and site operations. We do not sell your personal information. We may disclose information when required by law or to protect members, the platform, or the public.',
    'We use Google Analytics through Firebase to understand how the site is used — which pages people visit, how they arrived, and roughly what region they are in. This is collected using cookies and similar browser storage and is tied to a random identifier rather than your name. Your browser also stores what is needed to keep you signed in. You can block or clear cookies in your browser settings, though signing in will not work without them. We do not use this information for advertising, and we do not sell it.',
    'You may update much of your profile information from your account settings. You may contact us through the Contact Us page with privacy questions or requests. We retain information as needed to operate the service, meet legal obligations, and resolve disputes. By creating an account or using the site, you acknowledge this Privacy Policy.',
  ],
};

export function normalizePageSections(sections) {
  const normalized = Array.isArray(sections)
    ? sections.map(section => (section ?? '').toString())
    : [];

  while (normalized.length < SECTION_PARAGRAPH_COUNT) {
    normalized.push('');
  }

  return normalized.slice(0, SECTION_PARAGRAPH_COUNT);
}

export function migrateContentToSections(content) {
  if (Array.isArray(content)) {
    return normalizePageSections(content);
  }

  const text = stripHtmlToText(content || '');
  if (!text.trim()) {
    return normalizePageSections([]);
  }

  const parts = text
    .split(/\n\s*\n/)
    .map(part => part.trim())
    .filter(Boolean);

  const sections = normalizePageSections([]);
  parts.slice(0, SECTION_PARAGRAPH_COUNT).forEach((part, index) => {
    sections[index] = part;
  });

  if (parts.length === 1) {
    sections[0] = parts[0];
  }

  return sections;
}

export function stripHtmlToText(content) {
  if (!content) return '';
  if (typeof content !== 'string') return '';

  return content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
}

export function isStructuredSection(section) {
  return Boolean(
    section
    && typeof section === 'object'
    && !Array.isArray(section)
    && ('paragraphs' in section || 'bullets' in section || 'title' in section || 'paragraphsAfterBullets' in section)
  );
}

export function normalizeStructuredSection(section = {}) {
  return {
    title: (section.title || '').toString(),
    paragraphs: Array.isArray(section.paragraphs)
      ? section.paragraphs.map((paragraph) => paragraph.toString())
      : [],
    bullets: Array.isArray(section.bullets)
      ? section.bullets.map((bullet) => bullet.toString())
      : [],
    paragraphsAfterBullets: Array.isArray(section.paragraphsAfterBullets)
      ? section.paragraphsAfterBullets.map((paragraph) => paragraph.toString())
      : [],
  };
}

export function normalizeStructuredSections(sections) {
  if (!Array.isArray(sections) || sections.length === 0) {
    return DEFAULT_SITE_CONTENT.rules.map(normalizeStructuredSection);
  }

  if (isStructuredSection(sections[0])) {
    return sections.map(normalizeStructuredSection);
  }

  return DEFAULT_SITE_CONTENT.rules.map(normalizeStructuredSection);
}

export function normalizeRulesContent(rawSections, rawContent) {
  if (Array.isArray(rawSections) && rawSections.length > 0) {
    if (isStructuredSection(rawSections[0])) {
      return normalizeStructuredSections(rawSections);
    }
  }

  if (rawContent) {
    return normalizeStructuredSections(DEFAULT_SITE_CONTENT.rules);
  }

  return normalizeStructuredSections(DEFAULT_SITE_CONTENT.rules);
}

export function normalizePageContent(pageId, rawContent, rawSections) {
  if (STRUCTURED_PAGE_IDS.includes(pageId)) {
    return normalizeRulesContent(rawSections, rawContent);
  }

  if (MULTI_SECTION_PAGE_IDS.includes(pageId)) {
    if (Array.isArray(rawSections)) {
      return normalizePageSections(rawSections);
    }
    return migrateContentToSections(rawContent ?? DEFAULT_SITE_CONTENT[pageId]);
  }

  if (typeof rawContent === 'string' && rawContent.trim()) {
    return stripHtmlToText(rawContent);
  }

  return DEFAULT_SITE_CONTENT[pageId] || '';
}